import React, { FunctionComponent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './SchoolDetail.module.scss';
import Text from 'common/components/Text/Text';
import Loader from 'common/components/Loader/Loader';

import { useGetBackofficeSchoolQuery, useDeleteSchoolMutation } from 'services/exploreApi';
import { useParams } from 'react-router-dom';
import { Label } from 'app/types';

const SchoolDetail: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const { data: school, isLoading } = useGetBackofficeSchoolQuery(parseInt(id || ''));
  const [deleteSchool] = useDeleteSchoolMutation();
  const navigate = useNavigate();
  const handleDelete = async () => {
    await deleteSchool(school?.data.id);
    navigate('/backoffice/schools');
  };
  if (isLoading) return <Loader />;

  return (
    <div className={styles.container}>
      <div className={styles.firstWrap}>
        <Text size={40} weight={700}>
          {school?.data.name}
        </Text>
        <div className={styles.buttonsWrap}>
          <Link className={`${styles.exitButton} btn`} to="edit">
            Edit
          </Link>
          <Link className={`${styles.exitButton} btn ${styles.outline}`} to="edit/photos">
            Add/Edit Photos
          </Link>
          <button
            className={`${styles.exitButton} btn ${styles.outlineDanger}`}
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </div>
      <div className={styles.info}>
        <div className={styles.element}>
          <label htmlFor="city">City</label>
          <Text size={18} weight={400}>
            {school?.data.city}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="is_partner">Is Partner</label>
          <Text size={18} weight={400}>
            {school?.data.is_partner ? 'Yes' : 'No'}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="number_of_students">Number of Students</label>
          <Text size={18} weight={400}>
            {school?.data.number_of_students}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="number_of_international_students">Number of International Students</label>
          <Text size={18} weight={400}>
            {school?.data.number_of_international_students}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="employability_score">Employability Score</label>
          <Text size={18} weight={400}>
            {school?.data.employability_score}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="overview">Overview</label>
          <Text size={18} weight={400}>
            {school?.data.overview}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="about">About</label>
          <Text size={18} weight={400}>
            {school?.data.about}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="website">Website</label>
          <Text size={18} weight={400}>
            <a href={school?.data.website} target="_blank" rel="noreferrer">
              {school?.data.website}
            </a>
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="twitter_link">Twitter Link</label>
          <Text size={18} weight={400}>
            <a href={school?.data.twitter_link} target="_blank" rel="noreferrer">
              {school?.data.twitter_link}
            </a>
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="facebook_link">Facebook Link</label>
          <Text size={18} weight={400}>
            <a href={school?.data.facebook_link} target="_blank" rel="noreferrer">
              {school?.data.facebook_link}
            </a>
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="linkedin_link">LinkedIn Link</label>
          <Text size={18} weight={400}>
            <a href={school?.data.linkedin_link} target="_blank" rel="noreferrer">
              {school?.data.linkedin_link}
            </a>
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="why_study_here">Why Study Here</label>
          <Text size={18} weight={400}>
            {school?.data.why_study_here}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="scholarship_offer">Scholarship Offer</label>
          <Text size={18} weight={400}>
            {school?.data.scholarship_offer ? 'Yes' : 'No'}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="accommodation_offer">Accommodation Offer</label>
          <Text size={18} weight={400}>
            {school?.data.accommodation_offer ? 'Yes' : 'No'}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="established_year">Established Year</label>
          <Text size={18} weight={400}>
            {school?.data.established_year}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="average_tuition">Average Tuition</label>
          <Text size={18} weight={400}>
            {school?.data.average_tuition}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="average_program_year">Average Program Year</label>
          <Text size={18} weight={400}>
            {school?.data.average_program_year}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="priority">Priority</label>
          <Text size={18} weight={400}>
            {school?.data.priority}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="position_ranking">Position Ranking</label>
          <Text size={18} weight={400}>
            {school?.data.position_ranking}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="ranking_name">Ranking Name</label>
          <Text size={18} weight={400}>
            {school?.data.ranking_name}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="description">Description</label>
          <Text size={18} weight={400}>
            {school?.data.description}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="acceptance_rate">Acceptance Rate</label>
          <Text size={18} weight={400}>
            {school?.data.acceptance_rate}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="international_student_description">
            International Student Description
          </label>
          <Text size={18} weight={400}>
            {school?.data.international_student_description}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="learning_and_teaching_description">
            Learning and Teaching Description
          </label>
          <Text size={18} weight={400}>
            {school?.data.learning_and_teaching_description}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="application_description">Application Description</label>
          <Text size={18} weight={400}>
            {school?.data.application_description}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="working_after_months">Working After Months</label>
          <Text size={18} weight={400}>
            {school?.data.working_after_months}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="working_after_percentage">Working After Percentage</label>
          <Text size={18} weight={400}>
            {school?.data.working_after_percentage}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="alumni_information">Alumni Information</label>
          <Text size={18} weight={400}>
            {school?.data.alumni_information}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="career_service_description">Career Service Description</label>
          <Text size={18} weight={400}>
            {school?.data.career_service_description}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="student-support-description">Student Support Description</label>
          <Text size={18} weight={400}>
            {school?.data.student_support_description}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="application-fee">Application Fee</label>
          <Text size={18} weight={400}>
            {school?.data.application_fee}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="application-fee-currency">Application Fee Currency</label>
          <Text size={18} weight={400}>
            {school?.data.application_fee_currency}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="is-visible">Is Visible</label>
          <Text size={18} weight={400}>
            {school?.data.is_visible}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="employability-time">Employability Time</label>
          <Text size={18} weight={400}>
            {school?.data.employability_time}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="assessment-text">Assessment Text</label>
          <Text size={18} weight={400}>
            {school?.data.assessment_text}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="requirement-document-description">Requirement Document Description</label>
          <Text size={18} weight={400}>
            {school?.data.requirement_document_description}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="student-profile-description">Student Profile Description</label>
          <Text size={18} weight={400}>
            {school?.data.student_profile_description}
          </Text>
        </div>

        <div className={styles.element}>
          <label htmlFor="partner-type">Partner Type</label>
          <Text size={18} weight={400}>
            {school?.data.partner_type}
          </Text>
        </div>
        <div className={styles.options}>
          <Text size={20} weight={600}>
            Logo
          </Text>
          <img src={school?.data.logo} className={styles.logo} />
        </div>
        <div className={styles.options}>
          <Text size={20} weight={600}>
            Cover
          </Text>
          <img src={school?.data.cover} className={styles.logo} />
        </div>
        <div className={styles.options}>
          <Text size={20} weight={600}>
            Photos
          </Text>
          {school?.data.photos?.map((photo: string) => (
            <div key={photo} className={styles.imgContainer}>
              <img src={photo} className={styles.img} />
            </div>
          ))}
        </div>
        <div className={styles.element}>
          <Text size={20} weight={600}>
            Labels
          </Text>
          <div className="d-flex flex-column gap-4">
            {school?.data.labels?.map((label: Label) => (
              <div className="d-flex flex-column" key={label.id}>
                <Text size={18}>{label.tag.name}</Text>
                <Text size={18}>{label.description}</Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolDetail;
